import { useEffect, useState } from 'react'
import {
  supplierDataApi,
  type SupplierDataSource,
  type SupplierDataSourceState,
  type IntegrationStatus,
} from '../../api/endpoints'
import { ApiError } from '../../api/types'
import {
  DataSourceSetup, draftFromSaved, draftToSaveBody, draftIsSubmittable, proposalOf,
  type DataSourceDraft, EMPTY_DRAFT,
} from './DataSourceSetup'

/**
 * "Connect your inventory" — the My Stock home of the data-source setup a
 * supplier may have skipped at signup. Same DataSourceSetup as the wizard step,
 * and rendered by BOTH shells (desktop SellerMyStock + mobile SellerStockScreen),
 * so there is one implementation of the truth.
 *
 * ── THE CARD USED TO LIE (fixed 2026-08-19) ────────────────────────────────
 * It read "Sending stock via Supplier API — last test OK" the moment a URL was
 * saved. Nothing in `functions/src/integrations/` has ever read
 * `supplierDataSource`, so no stone was ever going to arrive from it. A supplier
 * seeing that stops chasing us, which is the most expensive thing this surface
 * can cause. It now renders the SERVER's status copy verbatim — including
 * "your stock is NOT live yet" — and the 7-day connect window as a countdown
 * that nags and never locks.
 */

const TONE: Record<string, string> = {
  idle: 'border-gold/40 bg-cream/60',
  act: 'border-gold-500/60 bg-gold-050',
  wait: 'border-line bg-paper',
  good: 'border-success/40 bg-success/5',
  bad: 'border-danger/40 bg-danger/5',
}

const METHOD_LABEL: Record<string, string> = {
  docs: 'API documentation',
  api: 'Supplier API feed',
  excel: 'Excel / CSV sheet',
  erp_push: 'ERP push',
}

/** Statuses where the ball is with the supplier — the only ones we nag on. */
const OURS_TO_FINISH: IntegrationStatus[] = ['not_started', 'draft', 'needs_input']

export function InventoryConnectCard({ enabled = true }: { enabled?: boolean }) {
  const [state, setState] = useState<SupplierDataSourceState | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [visible, setVisible] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<DataSourceDraft>(EMPTY_DRAFT)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    supplierDataApi.get()
      .then((s) => {
        if (cancelled) return
        setState(s)
        setLoaded(true)
        setVisible(true)
        // Nothing set up (or explicitly postponed) → open the editor — but ONLY
        // for someone who can actually change it. A plain org member gets the
        // "managed by your owner" note, never a read-only form they can't submit.
        const mayEdit = s.org ? s.org.canEdit : true
        setEditing(mayEdit && (s.status === 'not_started' || s.status === 'needs_input'))
        setDraft(draftFromSaved(s.dataSource))
      })
      .catch(() => { /* not a supplier / offline — render nothing */ })
    return () => { cancelled = true }
  }, [enabled])

  if (!enabled || !loaded || !state) return null
  if (!visible) return null

  const canEdit = state.org ? state.org.canEdit : true

  async function persist(): Promise<SupplierDataSource | null> {
    const check = draftIsSubmittable(draft)
    if (!check.ok) { setError(check.reason!); return null }
    const { dataSource } = await supplierDataApi.save(draftToSaveBody(draft))
    // The mapping is its own record: answers, the media pattern, and the human
    // tick that says a price may be read off this.
    if (proposalOf(draft) || draft.mediaPattern.trim() || Object.keys(draft.answers).length) {
      await supplierDataApi.saveMapping({
        answers: draft.answers,
        ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
        ...(draft.mediaPattern.trim() ? { mediaPattern: draft.mediaPattern.trim() } : {}),
        confirmed: draft.mappingConfirmed,
      })
    }
    return dataSource
  }

  async function save() {
    if (!draft.method) { setError('Pick how your stock reaches us.'); return }
    setBusy(true)
    setError(null)
    try {
      const saved = await persist()
      if (!saved) return
      const fresh = await supplierDataApi.get()
      setState(fresh)
      setDraft(draftFromSaved(fresh.dataSource))
      setJustSaved('Saved. Submit it when you’re ready and our team picks it up.')
      window.setTimeout(() => setJustSaved(null), 5000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save — try again.')
    } finally {
      setBusy(false)
    }
  }

  async function submit() {
    if (!draft.method) { setError('Pick how your stock reaches us.'); return }
    setBusy(true)
    setError(null)
    try {
      const saved = await persist()
      if (!saved) return
      await supplierDataApi.submit()
      const fresh = await supplierDataApi.get()
      setState(fresh)
      setDraft(draftFromSaved(fresh.dataSource))
      setEditing(false)
      setJustSaved('Sent to our team. We’ll email you when your stock is live.')
      window.setTimeout(() => setJustSaved(null), 6000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit — try again.')
    } finally {
      setBusy(false)
    }
  }

  const { status, statusCopy, deadline, dataSource } = state
  const review = dataSource?.review
  const nag = deadline.applies && OURS_TO_FINISH.includes(status) && deadline.daysLeft != null

  // Icon-tile tone follows the same status semantics as the card wash.
  const goodTone = statusCopy.tone === 'good'
  const badTone = statusCopy.tone === 'bad'

  return (
    <div
      className={`mb-8 rounded-card border p-5 shadow-elev-2 ${TONE[statusCopy.tone] ?? TONE.idle}`}
      data-testid="inventory-connect-card"
      data-status={status}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3.5">
          {/* status-toned icon tile — an inventory/box glyph */}
          <span
            className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-[10px]"
            style={
              goodTone
                ? { background: 'rgba(46,125,91,0.14)', color: 'var(--success)', border: '1px solid rgba(46,125,91,0.35)' }
                : badTone
                  ? { background: 'rgba(197,48,48,0.12)', color: 'var(--danger)', border: '1px solid rgba(197,48,48,0.3)' }
                  : { background: 'rgba(201,169,74,0.14)', color: 'var(--gold-600)', border: '1px solid rgba(201,169,74,0.4)' }
            }
            aria-hidden
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="M3.3 7 12 12l8.7-5" /><path d="M12 22V12" />
            </svg>
          </span>
          <div className="min-w-0">
            <h2 className="font-serif text-xl font-semibold text-ink">Connect your inventory</h2>
            <p className="mt-1 text-sm font-medium text-ink" data-testid="inventory-connect-status">
              {statusCopy.title}
              {dataSource?.method && dataSource.method !== 'later' && (
                <span className="font-normal text-muted"> · via {METHOD_LABEL[dataSource.method] ?? dataSource.method}</span>
              )}
            </p>
            <p className="mt-0.5 text-sm text-muted">{statusCopy.blurb}</p>
          </div>
        </div>
        {status !== 'not_started' && canEdit && (
          <button
            type="button"
            onClick={() => { setEditing((v) => !v); setError(null) }}
            className="shrink-0 text-xs font-semibold uppercase tracking-wide text-soft hover:text-ink min-h-[44px] px-2"
            data-testid="inventory-connect-edit"
          >
            {editing ? 'Close' : 'Edit'}
          </button>
        )}
      </div>

      {/* The 7-day expectation. A countdown, never a gate — a supplier locked
          out for missing an onboarding SLA cannot fix what they're locked out of. */}
      {nag && (
        <p
          className={`mt-3 text-sm ${deadline.overdue ? 'text-danger font-medium' : 'text-muted'}`}
          data-testid="inventory-connect-deadline"
        >
          {deadline.overdue
            ? `Your ${deadline.windowDays}-day setup window has passed — until this is connected, buyers can’t see any of your stock.`
            : `Suppliers connect within ${deadline.windowDays} days of signing up — ${deadline.daysLeft} ${deadline.daysLeft === 1 ? 'day' : 'days'} left.`}
        </p>
      )}

      {/* What the reviewer asked for. A request for changes the supplier can't
          read is the same as no reply at all. */}
      {status === 'needs_input' && review?.message && (
        <p className="mt-3 rounded-md border border-gold-500/50 bg-paper p-3 text-sm text-ink" data-testid="inventory-connect-review">
          <span className="font-semibold">From our team:</span> {review.message}
        </p>
      )}
      {status === 'rejected' && review?.message && (
        <p className="mt-3 rounded-md border border-danger/40 bg-paper p-3 text-sm text-ink" data-testid="inventory-connect-review">
          <span className="font-semibold">Why:</span> {review.message}
        </p>
      )}

      {!canEdit && (
        <p className="mt-3 text-sm text-muted" data-testid="inventory-connect-readonly">
          Your company’s stock connection is managed by your organization’s owner.
        </p>
      )}

      {justSaved && (
        <p className="mt-3 text-sm text-success font-medium" data-testid="inventory-connect-saved">{justSaved}</p>
      )}

      {editing && canEdit && (
        <div className="mt-5">
          <DataSourceSetup draft={draft} onChange={setDraft} canEdit={canEdit} />
          {error && <p className="mt-3 text-sm text-danger" role="alert">{error}</p>}
          {canEdit && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => void submit()}
                disabled={busy}
                className="inline-flex h-11 items-center justify-center rounded-full bg-navy px-6 text-sm font-semibold text-white shadow-card hover:bg-navy/90 active:scale-[0.98] transition-all disabled:opacity-50"
                data-testid="inventory-connect-submit"
              >
                {busy ? 'Sending…' : status === 'needs_input' ? 'Send updated details' : 'Send to our team'}
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy}
                className="text-sm font-semibold text-soft hover:text-ink min-h-[44px] disabled:opacity-50"
                data-testid="inventory-connect-save"
              >
                Save for later
              </button>
              {status !== 'not_started' && (
                <button
                  type="button"
                  onClick={() => { setEditing(false); setDraft(draftFromSaved(dataSource)); setError(null) }}
                  className="text-sm text-soft hover:text-ink min-h-[44px]"
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
